import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const CollectionsSection = ({ collections }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const checkScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);
  
  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        ref.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [checkScroll, collections]);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const cardWidth = window.innerWidth >= 768 ? 280 : 200;
      scrollRef.current.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    }
  };
  
  if (collections.length === 0) return null;
  
  const getCardStyle = (index, isMobile = false) => {
    if (isMobile) {
      return { height: '280px', marginTop: '0px' };
    }
    if (index % 2 === 1) {
      return { height: '360px', marginTop: '0px' };
    } else {
      return { height: '300px', marginTop: '30px' };
    }
  };
  
  return (
    <section className="py-10 md:py-14 bg-[#F5F5F5]" data-testid="collections-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a3a5c] mb-2">COLLECTIONS</h2>
          <p className="text-gray-600 text-sm md:text-base">Find your style. Explore our diverse collections!</p>
        </div>
        
        {/* DESKTOP */}
        <div className="hidden md:block relative overflow-hidden">
          {canScrollLeft && (
            <button onClick={() => scroll('left')} className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/90 rounded-full shadow-xl hover:bg-white transition-all border border-gray-100" aria-label="Previous collections">
              <ChevronLeft className="h-6 w-6 text-[#1a3a5c]" />
            </button>
          )}
          {canScrollRight && (
            <button onClick={() => scroll('right')} className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/90 rounded-full shadow-xl hover:bg-white transition-all border border-gray-100" aria-label="Next collections">
              <ChevronRight className="h-6 w-6 text-[#1a3a5c]" />
            </button>
          )}
          
          <div ref={scrollRef} className="flex items-start gap-5 overflow-x-auto scrollbar-hide scroll-smooth pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory' }}>
            {collections.map((collection, index) => {
              const cardStyle = getCardStyle(index, false);
              return (
                <div key={collection.id} className="flex-shrink-0" style={{ marginTop: cardStyle.marginTop, width: 'calc(25% - 15px)', minWidth: '200px', scrollSnapAlign: 'start' }}>
                  <Link to={collection.link} className="group block relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500" data-testid={`collection-${collection.id}`}>
                    <div className="relative w-full" style={{ height: cardStyle.height }}>
                      <img src={collection.image} alt={collection.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={collection.focal_x != null ? { objectPosition: `${collection.focal_x}% ${collection.focal_y ?? 50}%` } : undefined} loading="lazy" draggable="false" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h3 className="font-serif text-lg lg:text-xl font-bold text-white tracking-wide">{collection.name}</h3>
                        {collection.subtitle && <p className="text-white/80 text-xs mt-0.5">{collection.subtitle}</p>}
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* MOBILE */}
        <div className="md:hidden">
          <div className="grid grid-cols-2 gap-3">
            {collections.slice(0, 4).map((collection) => (
              <Link key={collection.id} to={collection.link} className="group block relative overflow-hidden rounded-xl shadow-md" data-testid={`collection-${collection.id}-mobile`}>
                <div className="aspect-square">
                  <img src={collection.image} alt={collection.name} className="w-full h-full object-cover" style={collection.focal_x != null ? { objectPosition: `${collection.focal_x}% ${collection.focal_y ?? 50}%` } : undefined} loading="lazy" draggable="false" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h3 className="font-serif text-sm font-bold text-white">{collection.name}</h3>
                    {collection.subtitle && <p className="text-white/70 text-[10px] mt-0.5">{collection.subtitle}</p>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        
        <div className="text-center mt-6 md:mt-8">
          <Link to="/products" className="inline-flex items-center justify-center px-8 py-3 border-2 border-[#C9A967] text-[#C9A967] rounded-full font-medium hover:bg-[#C9A967] hover:text-white transition-all duration-300">
            View All Collection
          </Link>
        </div>
      </div>
    </section>
  );
};
